import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginResultDto } from '../Models/Dtos/User/login-result-dto';
import { environment } from '../../environments/environment.development';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { LoginDto } from '../Models/Dtos/User/login-dto';
import { Router } from '@angular/router';
import { JwtPayload } from '../Models/Helpers/jwt-payload';
import { ProfileService } from './profile-service';
import { ProfileViewDto } from '../Models/Dtos/User/profile-view-dto';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private storageKey = 'skillProof_token';

  constructor(
    private http: HttpClient,
    private router: Router,
    private profileService: ProfileService,
  ) {}

  login(loginDto: LoginDto): Observable<LoginResultDto> {
    return this.http.post<LoginResultDto>(environment.apiUrls.login, loginDto).pipe(
      tap((res) => {
        this.saveToken(res.token);

        const userId = this.getUserId();

        if (userId) {
          this.profileService.loadProfile(userId);
        }
      }),
    );
  }

  saveToken(token: string) {
    localStorage.setItem(this.storageKey, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.storageKey);
  }

  logout() {
    localStorage.removeItem(this.storageKey);
    this.profileService.clearProfile();
    window.location.href = '/home';
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const payload = this.decodePayload(token);
    if (!payload) return false;
    if (payload.exp) {
      const now = Math.floor(Date.now() / 1000);
      return now < payload.exp;
    }
    return true;
  }

  decodePayload(token: string): any | null {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  getRoles(): string[] {
    const token = this.getToken();
    if (!token) return [];

    const payload = this.getPayload(token);
    if (!payload) return [];

    const roles: string[] = [];

    for (const key in payload) {
      if (key.endsWith('/role')) {
        const value = (payload as any)[key];
        if (Array.isArray(value)) {
          roles.push(...value);
        } else {
          roles.push(value);
        }
      }
    }
    return roles;
  }

  isAdmin(): boolean {
    return this.getRoles().includes('Admin');
  }

  getUserId(): string | null {
    const token = this.getToken();
    if (!token) return null;
    const payload = this.decodePayload(token);
    if (!payload) return null;

    const id = payload[AuthService.NAME_ID_CLAIM];
    return typeof id === 'string' && id.trim() !== '' ? id : null;
  }

  public static readonly NAME_ID_CLAIM =
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';

  private getPayload(token: string): JwtPayload | null {
    try {
      const base64url = token.split('.')[1] ?? '';
      const json = this.base64UrlDecode(base64url);
      return JSON.parse(json) as JwtPayload;
    } catch {
      return null;
    }
  }

  private base64UrlDecode(input: string): string {
    const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
    const s = atob(base64 + pad);
    return decodeURIComponent(
      s
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
  }

  hasRole(required: string | string[]): boolean {
    const have = this.getRoles().map(r => r.toUpperCase())
    const need = (Array.isArray(required) ? required : [required]).map(r => r.toUpperCase())
    return need.some(r => have.includes(r))
  }
}
